// src/hooks/useBranchModules.js
import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import { useBranch, useUpdateBranch } from './useBranches';
import { useMyChildren } from './useChildren';

/** Module keys - all optional modules that can be toggled per branch */
export const MODULE_KEYS = ['rozliczenia', 'chat', 'media', 'grafik', 'jadlospis'];

/** Default: all modules enabled (backwards compatible) */
const DEFAULT_MODULES = Object.fromEntries(MODULE_KEYS.map((k) => [k, true]));

/** Check if a module is enabled (default true when absent) */
const isEnabled = (modules, key) => modules?.[key] !== false;

/**
 * Hook for branch admin: get and update module toggles for a branch.
 * @param {string} branchId
 * @returns {{ modules: object, isModuleEnabled: (key) => boolean, updateModule: (key, enabled) => Promise<void>, isLoading: boolean }}
 */
export function useBranchModules(branchId, options = {}) {
  const { data: branch, isLoading } = useBranch(branchId, { enabled: !!branchId, ...options });
  const updateBranch = useUpdateBranch();
  const queryClient = useQueryClient();

  const modules = useMemo(() => {
    const raw = branch?.settings?.modules;
    return { ...DEFAULT_MODULES, ...(raw || {}) };
  }, [branch?.settings?.modules]);

  const isModuleEnabled = (key) => isEnabled(modules, key);

  const updateModule = async (key, enabled) => {
    if (!branchId || !branch) return;
    const newModules = { ...modules, [key]: enabled };
    await updateBranch.mutateAsync({
      id: branchId,
      settings: { ...(branch.settings || {}), modules: newModules }
    });
    queryClient.invalidateQueries({ queryKey: ['branches'] });
  };

  return {
    modules,
    isModuleEnabled,
    updateModule,
    isLoading
  };
}

/**
 * For parents: fetch branches that have the parent's children, then merge module state.
 * Module is enabled if ANY of the parent's children's branches has it enabled.
 */
function useParentBranchModules(userId) {
  const { data: children = [] } = useMyChildren(userId, { enabled: !!userId });
  const branchIds = useMemo(
    () => [...new Set(children.map((c) => c.branch_id).filter(Boolean))].sort(),
    [children]
  );

  const branchesQuery = useQuery({
    queryKey: ['branches', 'for-parent', branchIds],
    queryFn: async () => {
      if (branchIds.length === 0) return [];
      const { data, error } = await supabase
        .from('branches')
        .select('id, settings')
        .in('id', branchIds);
      if (error) throw error;
      return data || [];
    },
    enabled: branchIds.length > 0
  });

  const merged = useMemo(() => {
    const branches = branchesQuery.data || [];
    const mergedModules = {};
    for (const key of MODULE_KEYS) {
      // Show module if ANY branch has it enabled (not explicitly false)
      mergedModules[key] = branches.some((b) => (b.settings?.modules || {})[key] !== false);
    }
    return mergedModules;
  }, [branchesQuery.data]);

  const isModuleEnabled = (key) => isEnabled(merged, key);

  return {
    modules: merged,
    isModuleEnabled,
    isLoading: branchesQuery.isLoading
  };
}

/**
 * Context-aware hook: returns enabled modules for the current user.
 * - branch_admin / teacher: current branch's modules
 * - parent: merged modules from all branches where they have children (union: show if any branch enables)
 */
export function useEnabledModulesForUser() {
  const { userProfile } = useAuth();
  const role = userProfile?.active_role || userProfile?.roles?.[0] || 'parent';
  const branchId = userProfile?.current_branch_id || userProfile?.branch_ids?.[0];
  const userId = userProfile?.id;

  const branchModules = useBranchModules(branchId, {
    enabled: (role === 'branch_admin' || role === 'teacher') && !!branchId
  });

  const parentModules = useParentBranchModules(userId);

  if (role === 'parent') {
    return {
      modules: parentModules.modules,
      isModuleEnabled: parentModules.isModuleEnabled,
      isLoading: parentModules.isLoading
    };
  }

  return {
    modules: branchModules.modules,
    isModuleEnabled: branchModules.isModuleEnabled,
    isLoading: branchModules.isLoading
  };
}
